
/**
 * Validates and sanitizes URLs for profile links
 */
export const validateProfileUrl = (url: string): { isValid: boolean; sanitizedUrl: string } => {
  try {
    // Don't validate gradients
    if (url.includes('linear-gradient') || url.includes('radial-gradient')) {
      return { isValid: true, sanitizedUrl: url };
    }

    // Handle empty or invalid input
    if (!url || typeof url !== 'string') {
      return { isValid: false, sanitizedUrl: '' };
    }

    // Remove whitespace
    let cleanedUrl = url.trim();

    // Remove multiple protocols (e.g., https://https://)
    cleanedUrl = cleanedUrl.replace(/^(https?:\/\/)+(.*)/i, '$1$2');

    // Only add https:// if no protocol exists - this is the key change
    if (!/^https?:\/\//i.test(cleanedUrl)) {
      cleanedUrl = 'https://' + cleanedUrl;
    }

    // Test URL validity
    try {
      new URL(cleanedUrl);
    } catch (error) {
      console.warn('Invalid URL format:', cleanedUrl);
      return { isValid: false, sanitizedUrl: '' };
    }
    
    // Encode special characters but preserve valid URL characters
    try {
      const urlObj = new URL(cleanedUrl);
      // IMPORTANT FIX: Use encodeURIComponent instead of encodeURI for better special character handling
      // Keep forward slashes as-is
      try {
        // First try to decode to avoid double-encoding
        const decodedPath = decodeURIComponent(urlObj.pathname);
        urlObj.pathname = encodeURIComponent(decodedPath).replace(/%2F/g, '/');
      } catch (decodeError) {
        // If decoding fails, just encode directly
        urlObj.pathname = encodeURIComponent(urlObj.pathname).replace(/%2F/g, '/');
      }
      
      return {
        isValid: true,
        sanitizedUrl: urlObj.toString()
      };
    } catch (encodeError) {
      console.error('Error encoding URL:', encodeError);
      // If encoding fails, still return the cleaned URL if it parsed successfully
      return {
        isValid: true,
        sanitizedUrl: cleanedUrl
      };
    }
  } catch (e) {
    console.warn('Invalid URL:', url, e);
    return {
      isValid: false,
      sanitizedUrl: ''
    };
  }
};

/**
 * Sanitizes URLs that are already in the system
 */
export const sanitizeExistingUrl = (url: string): string => {
  try {
    const result = validateProfileUrl(url);
    return result.isValid ? result.sanitizedUrl : '';
  } catch (e) {
    console.error('Error sanitizing URL:', e);
    return '';
  }
};
